import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { getJunkRequisitions } from '../../../models/requisitionModel';
import { toast } from 'react-toastify';

function Junk() {
  const [requisitions, setRequisitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const { can, user } = useAuth();
  const navigate = useNavigate();

  // Fetch junk requisitions
  const fetchRequisitions = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        pageSize: pageSize
      };
      
      if (searchTerm) params.filter = searchTerm;
      
      const response = await getJunkRequisitions(params);
      
      if (response.status === 1 && response.data) {
        const responseData = response.data.data || [];
        const meta = response.data.meta || {};
        
        setRequisitions(responseData);
        const actualTotal = meta.totalItems || responseData.length;
        setTotalCount(actualTotal);
      } else {
        console.error('Failed API response:', response);
        toast.error(response.message || 'Failed to load junk requisitions');
        setRequisitions([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('An error occurred while loading junk requisitions');
      setRequisitions([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm]);

  useEffect(() => {
    fetchRequisitions();
  }, [fetchRequisitions]);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const totalPages = Math.ceil(totalCount / pageSize);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  const formatDate = (timestamp) => {
    if (!timestamp || timestamp === null || timestamp === '0' || timestamp === 0) return 'N/A';
    
    // If it's already in YYYY-MM-DD format, convert to display format
    if (typeof timestamp === 'string' && timestamp.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const date = new Date(timestamp);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('en-GB');
      }
    }
    
    // Handle numeric timestamps
    const date = Number.isInteger(parseInt(timestamp))
      ? new Date(parseInt(timestamp) * 1000)
      : new Date(timestamp);
      
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return 'N/A';
    }
    
    // Check if it's the Unix epoch (1970-01-01) which indicates invalid data
    if (date.getFullYear() === 1970 && date.getMonth() === 0 && date.getDate() === 1) {
      return 'N/A';
    }
    
    return date.toLocaleDateString('en-GB');
  };

  const getStatusDisplay = (req) => {
    // For junk requisitions, show specific status
    return <span className="inline-block px-3 py-1 rounded font-semibold text-xs bg-red-100 text-red-800">Junk</span>;
  };

  if (loading && requisitions.length === 0) {
    return (
      <div className="p-6 md:p-10 flex justify-center items-center h-[calc(100vh-150px)]">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-2 py-1">
      {/* Professional Header Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
        <div className="flex items-start justify-between">
          {/* Left: Icon, Title, Description, Results */}
          <div className="flex items-start">
            <div className="flex-shrink-0 rounded-lg p-2.5 mr-3 mt-0.5" style={{ backgroundColor: 'rgba(220, 38, 38, 0.15)' }}>
              <svg className="h-5 w-5" fill="none" stroke="#dc2626" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">Junk</h1>
              <p className="text-xs text-gray-500 mb-1">
                Discarded Requisitions
              </p>
              <div className="flex items-center mt-1">
                <span className="text-xs font-medium" style={{ color: '#0096DE' }}>
                  {totalCount ? `Showing ${Math.min(pageSize, totalCount)} of ${totalCount} results` : '0 results'}
                  {searchTerm && (
                    <span className="ml-1 text-blue-600">
                      (filtered)
                    </span>
                  )}
                </span>
              </div>
            </div>
          </div>
          
          {/* Right: All Controls and Search */}
          <div className="flex flex-col gap-2 items-end min-w-0">
            {/* Row 1: All Controls on Right */}
            <div className="flex items-center gap-2 flex-wrap">
              {/* Clear Filters Button (only show if filters are active) */}
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="inline-flex items-center px-2 py-1.5 border border-red-300 rounded-md shadow-sm text-xs text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-1 focus:ring-red-500 flex-shrink-0"
                >
                  Clear Filters
                </button>
              )}
              
              {/* Refresh Button */}
              <button 
                onClick={() => fetchRequisitions()} 
                className="inline-flex items-center px-2 py-1.5 border border-gray-300 shadow-sm text-sm rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 flex-shrink-0"
                title="Refresh"
                disabled={loading}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke={loading ? '#0096DE' : 'currentColor'} style={{ color: loading ? '#0096DE' : 'currentColor' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>

              {/* Page Size Filter */}
              <div className="flex-shrink-0">
                <select
                  value={pageSize}
                  onChange={handlePageSizeChange}
                  className="text-sm px-2 py-1.5 border border-gray-300 bg-white rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  {[10, 20, 30, 50].map(size => (
                    <option key={size} value={size}>{size} per page</option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Search Box in Row 2 */}
            <div className="relative w-full">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                </svg>
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearch}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Search junk requisitions..."
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Simple Table */}
      <div className="bg-white overflow-hidden rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-gray-200">
            <thead style={{ backgroundColor: 'rgba(0, 135, 212, 0.08)' }}>
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>ID</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>Req Number</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>Project Name</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>User</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>Department</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>Created</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>Junk Reason</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>Status</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading && (
                <tr>
                  <td colSpan="9" className="px-3 py-4 text-center">
                    <div className="flex items-center justify-center space-x-2">
                      <svg className="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className="text-sm">Loading...</span>
                    </div>
                  </td>
                </tr>
              )}
              {!loading && requisitions.length === 0 && (
                <tr>
                  <td colSpan="9" className="px-3 py-6 text-center">
                    <p className="text-gray-600">No junk requisitions found</p>
                  </td>
                </tr>
              )}
              {!loading && requisitions.map(req => {
                const displayUser = req.user_full_name || req.requester_name || (req.user_id ? `User #${req.user_id}` : 'Unknown');
                
                return (
                  <tr key={req.requisition_id} className="hover:bg-red-50">
                    <td className="px-3 py-2 text-sm">{req.requisition_id}</td>
                    <td className="px-3 py-2 text-sm">EPR-{req.requisition_number}</td>
                    <td className="px-3 py-2 text-sm">{req.specification_title || 'Untitled Project'}</td>
                    <td className="px-3 py-2 text-sm">{displayUser}</td>
                    <td className="px-3 py-2 text-sm">{req.department_name || 'General'}</td>
                    <td className="px-3 py-2 text-sm">{formatDate(req.requisition_date)}</td>
                    <td className="px-3 py-2 text-sm">{req.requisition_junk_reason || 'N/A'}</td>
                    <td className="px-3 py-2 text-sm">{getStatusDisplay(req)}</td>
                    <td className="px-3 py-2 text-sm">
                      <Link
                        to={`/dashboard/requisitions/${req.requisition_id}`}
                        className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-xs"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {/* Enhanced Pagination */}
        {totalCount > 0 && (
          <div className="bg-gray-50 px-3 py-3 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
            <div className="flex items-center gap-4">
              <div className="text-gray-500">
                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} results
              </div>
              
              {/* Page Size Selector */}
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Show:</span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1); // Reset to first page when changing page size
                  }}
                  className="border border-gray-300 rounded px-2 py-1 text-xs bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  {[10, 20, 30, 50].map(size => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
                <span className="text-gray-500">per page</span>
              </div>
            </div>
            
            <div className="flex items-center space-x-1">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-3 py-1.5 border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 rounded text-xs font-medium"
              >
                First
              </button>
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 rounded text-xs font-medium"
              >
                Previous
              </button>
              
              {/* Page Numbers */}
              <div className="flex items-center space-x-1">
                {(() => {
                  const pageNumbers = [];
                  
                  // Show max 5 page numbers
                  let startPage = Math.max(1, currentPage - 2);
                  let endPage = Math.min(totalPages, startPage + 4);
                  
                  // Adjust start if we're near the end
                  if (endPage - startPage < 4) {
                    startPage = Math.max(1, endPage - 4);
                  }
                  
                  for (let i = startPage; i <= endPage; i++) {
                    pageNumbers.push(
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        className={`px-3 py-1.5 border rounded text-xs font-medium ${
                          i === currentPage
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {i}
                      </button>
                    );
                  }
                  
                  return pageNumbers;
                })()}
              </div>
              
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 rounded text-xs font-medium"
              >
                Next
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 rounded text-xs font-medium"
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Junk;
